import { Box } from '@deckee/deck-hand';
import React, { FC, useMemo } from 'react';
import { RainChart } from '../charts';
import {
  enrichedSeriesToTimeSeries,
  formatRainHeaderParts,
  getClosestEnrichedPoint,
  getForecastDayDomain,
  graphPointsToEnrichedSeries,
  type WeatherForecastGraphPoint,
} from '../../helpers/graph/graphData';
import { useGraphTime } from './GraphTimeContext';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import GraphCardHeader from './GraphCardHeader';
import GraphCardNoData from './GraphCardNoData';

export interface RainCardProps {
  rainfallPoints: WeatherForecastGraphPoint[];
  selectedDate: string;
  timeZone: string;
  latitude: number;
  longitude: number;
}

const RainCard: FC<RainCardProps> = ({
  rainfallPoints,
  selectedDate,
  timeZone,
  latitude,
  longitude,
}) => {
  const {
    selectedChartTime,
    defaultChartTime,
    handleSelectedChartTimeChange,
    handleChartScrubStart,
    handleChartScrubEnd,
  } = useGraphTime('rain');

  const rainfallSeries = useMemo(
    () => graphPointsToEnrichedSeries(rainfallPoints, selectedDate, timeZone),
    [rainfallPoints, selectedDate, timeZone],
  );

  const domain = useMemo(
    () => getForecastDayDomain(selectedDate, timeZone),
    [selectedDate, timeZone],
  );

  const chartData = useMemo(
    () => enrichedSeriesToTimeSeries(rainfallSeries, domain),
    [domain, rainfallSeries],
  );

  const selectedRainPoint = useMemo(() => {
    if (!selectedChartTime) {
      return undefined;
    }

    return getClosestEnrichedPoint(rainfallSeries, selectedChartTime);
  }, [rainfallSeries, selectedChartTime]);

  const rainHeader = formatRainHeaderParts(selectedRainPoint?.value);

  if (chartData.length === 0) {
    return (
      <CollapsibleCard
        title="Rain"
        testId="weather-forecast-rain-card"
        analyticsCard="rain"
      >
        <GraphCardNoData
          chartLabel="Rain"
          stationType="general"
          isLocationMissingData={rainfallPoints.length === 0}
          latitude={latitude}
          longitude={longitude}
          selectedDate={selectedDate}
        />
      </CollapsibleCard>
    );
  }

  return (
    <CollapsibleCard
      title="Rain"
      testId="weather-forecast-rain-card"
      analyticsCard="rain"
    >
      <GraphCardHeader primary={rainHeader.primary} />

      <Box width="100%">
        <RainChart
          data={chartData}
          domain={domain}
          timeZone={timeZone}
          selectedTime={selectedChartTime}
          defaultSelectedTime={defaultChartTime}
          onSelectedTimeChange={handleSelectedChartTimeChange}
          onScrubStart={handleChartScrubStart}
          onScrubEnd={handleChartScrubEnd}
        />
      </Box>
    </CollapsibleCard>
  );
};

export default RainCard;
